import { FC, Fragment, useMemo } from 'react';
import simpleColumnDefs from 'features/conversions/forms/views/formsSimpleColumnDefs';
import TileGridContainer from 'features/tiles/tileGridContainer';
import TileWithoutDefaultStyling from 'features/tiles/tileWithoutDefaultStyling';
import { PaginatedTable } from 'features/pagination/paginatedTable';
import { usePaginatedQuery } from 'features/pagination/hooks/usePaginatedQuery';
import {
  useGetConversionsExportMutation,
  useGetPaginatedConversionsQuery,
  usePrefetch
} from 'app/apis/queryApi';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'app/store';
import {
  toggleSelection,
  selectViewSelectionByComponentId
} from 'features/data-view-selection/dataViewSelectionSlice';
import { ViewType } from 'features/data-view-selection/view-type';
import { ConversionViewRequest } from 'features/conversions/forms/types/conversionSimpleViewRequest';
import getDetailedColumnDefs from 'features/conversions/forms/views/formsDetailColumnDefs';
import ConversionsInteractionBar from 'features/conversions/conversions-interaction-bar';
import { selectPaginationOptions } from 'features/pagination/paginationOptionsSlice';
import { ConversionWebExportRequest } from 'features/conversions/forms/types/conversion-web-export-request';
import { debugToggleByType } from 'features/internal/internal-settings.slice';
import { ToggleType } from 'features/internal/internal.models';
import { selectDateRangeLocalIso } from 'features/date-range-selection/dateRangeSlice';
import { ConversionDetailedViewResponse } from 'features/conversions/forms/types/converisonDetailedViewResponse';

const ConversionsForm: FC = () => {
  const dispatch = useDispatch();

  const { selection: viewType } = useSelector((state: RootState) =>
    selectViewSelectionByComponentId(state, 'forms-component')
  );

  const conversionIdToggle = useSelector((state: RootState) =>
    debugToggleByType(state, ToggleType.DisplayConversionId)
  );

  const formComponentId = useMemo(() => {
    return `forms-component-${viewType ?? 'simple'}`;
  }, [viewType]);

  const prefetchPage = usePrefetch('getPaginatedConversions');
  const {
    data: conversionResponse,
    isLoading: conversionRequestIsLoading,
    isFetching: conversionRequestIsFetching
  } = usePaginatedQuery<ConversionViewRequest, ConversionDetailedViewResponse>(
    useGetPaginatedConversionsQuery,
    prefetchPage,
    formComponentId,
    {
      conversionType: 'Web',
      viewType
    } as ConversionViewRequest,
    {
      initialColumnName: 'CaptureDateTime',
      initialOrderDirection: 'ASC'
    }
  );

  const columnDefs = useMemo(() => {
    if (
      conversionRequestIsLoading ||
      conversionRequestIsFetching ||
      !conversionResponse?.data
    ) {
      return [];
    }

    const columnDefs: any[] =
      viewType === ViewType.Simple
        ? simpleColumnDefs()
        : getDetailedColumnDefs(
            conversionResponse?.data ?? [],
            conversionIdToggle?.enabled ?? false
          );

    return columnDefs;
  }, [
    conversionRequestIsFetching,
    conversionRequestIsLoading,
    conversionResponse?.data,
    conversionIdToggle,
    viewType
  ]);

  const handleClick = (viewType: ViewType) => {
    dispatch(
      toggleSelection({
        newSelection: viewType,
        componentId: 'forms-component'
      })
    );
  };

  const { siteId } = useSelector((state: RootState) => state.selectedSite);
  const { startDate, endDate } = useSelector(selectDateRangeLocalIso);

  const paginationOptions = useSelector((state: RootState) =>
    selectPaginationOptions(state, siteId)
  );

  const exportRequest: ConversionWebExportRequest = {
    siteId,
    conversionType: 'Web',
    viewType,
    startDate,
    endDate,
    searchTerm: paginationOptions?.searchTerm ?? ''
  };

  const [triggerExport, { isLoading: isExporting }] =
    useGetConversionsExportMutation();

  const onTriggerExport = () => {
    void triggerExport(exportRequest);
  };

  return (
    <>
      <TileGridContainer>
        {viewType === 'detailed' ? (
          <Fragment key="forms-component-detailed">
            <ConversionsInteractionBar
              componentId={formComponentId}
              currentViewType={viewType}
              isExporting={isExporting}
              onChangeViewType={handleClick}
              onTriggerExport={onTriggerExport}
            />
            <TileWithoutDefaultStyling className="min-h-fit justify-center align-middle bg-white border rounded border-off-navy w-full my-2 grow basis-full">
              <PaginatedTable
                columns={columnDefs}
                data={conversionResponse?.data ?? []}
                isLoading={conversionRequestIsLoading}
                isFetching={conversionRequestIsFetching}
                componentId={formComponentId}
                getRowId={(row) => row.id}
                getRowData={() => null}
              />
            </TileWithoutDefaultStyling>
          </Fragment>
        ) : (
          <Fragment key="forms-component-simple">
            <ConversionsInteractionBar
              componentId={formComponentId}
              currentViewType={viewType ?? ViewType.Simple}
              isExporting={isExporting}
              onChangeViewType={handleClick}
              onTriggerExport={onTriggerExport}
            />
            <TileWithoutDefaultStyling className="min-h-fit justify-center align-middle bg-white border rounded border-off-navy w-full my-2 grow basis-full">
              <PaginatedTable
                columns={columnDefs}
                data={conversionResponse?.data ?? []}
                isLoading={conversionRequestIsLoading}
                isFetching={conversionRequestIsFetching}
                componentId={formComponentId}
                getRowId={(row, relativeIndex, parent) => {
                  return row.id;
                }}
                getRowData={() => null}
              />
            </TileWithoutDefaultStyling>
          </Fragment>
        )}
      </TileGridContainer>
    </>
  );
};

export default ConversionsForm;
